import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  AutocompleteField,
  DatePickerField,
  InputField,
  TextareaField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";

const optionalNumber = z.preprocess(
  (val) => (val === "" || val === null || val === undefined ? null : val),
  z.coerce.number().nullable().optional(),
);

const FormSchema = z.object({
  strategy_id: z.coerce.number(),
  strategy_name: z.union([
    z.string(),
    z.object({ text: z.string(), id: z.number() }),
  ]),
  investment_strategy: z.string().min(1),
  reference_indicator: z.string().min(1),
  sfdr_classification: z.string().nullable().optional(),
  entry_exit_fee: optionalNumber,
  max_management_fee: optionalNumber,
  minimum_subscription: z.string().nullable().optional(),
  depositary_name: z.string().nullable().optional(),
  valuator_name: z.string().nullable().optional(),
  frequency: z.string().nullable().optional(),
  feeder_fund_name: z.string().nullable().optional(),
  fund_manager_name: z.string().nullable().optional(),
  isr_score: optionalNumber,
  inception_date: z.preprocess(
    (val) => parseToDateValue(val as string),
    dateSchema.optional().nullable(),
  ),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

const formFields = FormSchema.keyof().enum;

export function S1InvestStrategiesEditCard({ id }: { id: number }) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
    options,
  } = useEditForm({
    id,
    client: Clients.S1_QUANTILIA,
    table_name: Tables.S1_INVEST_STRATEGIES,
    FormSchema,
    adjustData: (data) => {
      const { strategy_name, ...payload } = data;
      return payload;
    },
  });

  const tbl = Tables.S1_INVEST_STRATEGIES;

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${tbl}.title`, { ns: "tables", defaultValue: "Investment strategy" })} {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name={formFields.strategy_name}
                  label={t(`${tbl}.columns.strategy_name`, { ns: "tables" })}
                  base_url={`${Clients.S1_QUANTILIA}/${Tables.S1_INVEST_STRATEGIES}`}
                  column_name="name"
                  id_column={formFields.strategy_id}
                  control={form.control}
                  isRequired={true}
                  options={{ autoSelectSingle: true }}
                />

                <TextareaField
                  name={formFields.investment_strategy}
                  label={t(`${tbl}.columns.investment_strategy`, { ns: "tables" })}
                  isRequired={true}
                  control={form.control}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formFields.reference_indicator}
                    label={t(`${tbl}.columns.reference_indicator`, { ns: "tables" })}
                    isRequired={true}
                    control={form.control}
                  />
                  <InputField
                    name={formFields.sfdr_classification}
                    label={t(`${tbl}.columns.sfdr_classification`, { ns: "tables" })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteField
                    name={formFields.frequency}
                    label={t(`${tbl}.columns.frequency`, { ns: "tables" })}
                    options={options?.["frequency"] ?? []}
                    control={form.control}
                  />
                  <DatePickerField
                    name={formFields.inception_date}
                    label={t(`${tbl}.columns.inception_date`, { ns: "tables" })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formFields.entry_exit_fee}
                    label={t(`${tbl}.columns.entry_exit_fee`, { ns: "tables" })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name={formFields.max_management_fee}
                    label={t(`${tbl}.columns.max_management_fee`, { ns: "tables" })}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formFields.minimum_subscription}
                    label={t(`${tbl}.columns.minimum_subscription`, { ns: "tables" })}
                    control={form.control}
                  />
                  <InputField
                    name={formFields.depositary_name}
                    label={t(`${tbl}.columns.depositary_name`, { ns: "tables" })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formFields.valuator_name}
                    label={t(`${tbl}.columns.valuator_name`, { ns: "tables" })}
                    control={form.control}
                  />
                  <InputField
                    name={formFields.feeder_fund_name}
                    label={t(`${tbl}.columns.feeder_fund_name`, { ns: "tables" })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formFields.fund_manager_name}
                    label={t(`${tbl}.columns.fund_manager_name`, { ns: "tables" })}
                    control={form.control}
                  />
                  <InputField
                    name={formFields.isr_score}
                    label={t(`${tbl}.columns.isr_score`, { ns: "tables" })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={Clients.S1_QUANTILIA}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, ["strategy_id"]) : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
